import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "February 28, 2026";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
      <div className="label mb-4 text-[hsl(var(--accent))]">Privacy Policy</div>
      <h1 className="serif text-5xl text-foreground leading-tight">
        How we treat what you share with us.
      </h1>
      <p className="mt-4 mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        Last updated · {LAST_UPDATED}
      </p>

      <Section title="The short version">
        <p>
          Correlate is a small archive about the things we have in common across
          traditions. We collect only what we need to keep your account, your saves,
          and your conversations working. We do not sell your data. We do not show
          you ads.
        </p>
      </Section>

      <Section title="What we collect">
        <ul className="space-y-3 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>
            <strong>Account data</strong> — your email, display name, optional handle,
            optional profile picture, and a securely hashed password (we never store
            your password in plain text). If you sign in with Google, we receive
            your name, email, and profile picture from Google&apos;s OAuth response.
          </li>
          <li>
            <strong>Content you create</strong> — your Oracle conversations, saved
            correlations, weekly posts, comments, pins, reactions, direct messages,
            and your bio. Anything you choose to make public is publicly visible;
            anything kept private remains visible only to you.
          </li>
          <li>
            <strong>Direct messages</strong> — stored on our servers so you can read
            them across devices. They are not end-to-end encrypted. They are visible
            only to you and the recipient.
          </li>
          <li>
            <strong>Notifications</strong> — when you opt in to web push, we store an
            anonymous push subscription endpoint so we can deliver notifications.
            You can revoke this at any time in your browser settings or in Correlate
            Settings.
          </li>
          <li>
            <strong>Usage data</strong> — minimal server logs (IP address, request path,
            response code, timestamp) kept for debugging and abuse prevention. Not used
            for advertising.
          </li>
        </ul>
      </Section>

      <Section title="What we don't collect">
        <ul className="space-y-3 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>We do not run third-party advertising trackers.</li>
          <li>We do not sell your personal data to anyone, ever.</li>
          <li>We do not access your device&apos;s contacts, camera, microphone, or location.</li>
        </ul>
      </Section>

      <Section title="How we use what we collect">
        <ul className="space-y-3 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>To keep you signed in and remember your saves.</li>
          <li>To deliver notifications you have opted in to (email, web push, in-app bell).</li>
          <li>To answer your Oracle questions — your prompts are sent to a large
            language model provider (OpenAI / Anthropic / Google) over an
            authenticated API. Providers may retain prompts for abuse and safety
            review per their own policies. We do not use your conversations to
            train models.</li>
          <li>To prevent abuse, spam, and to honor lawful requests.</li>
        </ul>
      </Section>

      <Section title="Who we share with">
        <p className="mb-3">
          We use a small set of vetted infrastructure providers strictly for the
          operation of the service:
        </p>
        <ul className="space-y-3 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li><strong>MongoDB Atlas</strong> — primary database hosting.</li>
          <li><strong>OpenAI / Anthropic / Google</strong> — the large language
            models powering the Oracle.</li>
          <li><strong>Resend</strong> — transactional email (digests, account emails).</li>
          <li><strong>Emergent</strong> — application hosting + Google OAuth flow.</li>
        </ul>
        <p className="mt-3">
          Each provider sees only the data necessary for its specific function and is
          bound by its own privacy policy. We do not share data with advertisers.
        </p>
      </Section>

      <Section title="Your choices">
        <ul className="space-y-3 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li><strong>Profile visibility</strong> — toggle &ldquo;Public reading shelf&rdquo;
            in Settings to control whether other readers see your pins, reactions,
            and saves.</li>
          <li><strong>Direct messages</strong> — toggle &ldquo;Allow DMs&rdquo; in
            Settings to close your inbox to new conversations.</li>
          <li><strong>Email</strong> — opt out of digest + notification emails in
            Settings. You can also use the unsubscribe link in any email we send.</li>
          <li><strong>Push notifications</strong> — revoke in your browser
            permissions or from Settings.</li>
          <li><strong>Delete your account</strong> — write to us at the address below
            and we will delete your account and associated content within 30 days.</li>
        </ul>
      </Section>

      <Section title="Data retention">
        <p>
          We keep your account data and content for as long as your account is
          active. Server logs are kept for up to 90 days. If you delete your
          account, we remove your data within 30 days, except where law requires
          us to retain certain records (such as for fraud investigations).
        </p>
      </Section>

      <Section title="Children">
        <p>
          Correlate is not directed at children under 13. If you believe a child
          has provided us personal data, please contact us and we will delete it
          promptly.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We will update this page when our practices change. The &ldquo;Last
          updated&rdquo; date at the top reflects the most recent version. Material
          changes will be communicated by email or an in-app notice.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions or requests about your data may be sent to&nbsp;
          <a href="mailto:privacy@correlate.online" className="link-underline text-[hsl(var(--primary))]">
            privacy@correlate.online
          </a>.
        </p>
      </Section>

      <p className="mt-16 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Link to="/terms" className="link-underline">See also: Terms of Service →</Link>
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-2xl text-foreground mb-3">{title}</h2>
      <div className="serif text-foreground/85 leading-relaxed text-[16px]">
        {children}
      </div>
    </section>
  );
}
